import React from 'react';

import get from 'lodash/get';
import map from 'lodash/map';
import flow from 'lodash/flow';
import size from 'lodash/size';
import filter from 'lodash/filter';
import intersection from 'lodash/intersection';
import difference from 'lodash/difference';
import compact from 'lodash/compact';
import sortBy from 'lodash/sortBy';
import isEmpty from 'lodash/isEmpty';
import flatten from 'lodash/flatten';
import values from 'lodash/values';

import { connect } from 'react-redux';

import GridSectionDesktopLayout from './grid_section_desktop_layout.jsx';
import GridRendererDesktop from './grid_renderer_desktop.jsx';
import GridSectionMobileLayout from './grid_section_mobile_layout.jsx';

import {
  getRenderAsMobile,
  getSectionGridTaxonomy,
  getTileMobileOrderPriorityByUuid,
  getAppliedTagIdsByCategoryIdForSection
} from 'zoomforth_codesbane_viewer/reducers/index.js';

import {
  getNextAvailablePosition
} from 'zoomforth_codesbane_editor/lib/grid_positions.js';

import {
  getTileSpacingStyle
} from 'zoomforth_codesbane_viewer/lib/sections.js';

import {
  sortTiles,
  generateTileFromGridPosition
} from './tile_generators.js';

import {
  GRID_TAXONOMY_FILTER_LOGICAL_OPERATOR_OR
} from 'grid_taxonomy.js';

/**
 * getFilteredGridPositions is the champion behind content filtering in the
 * grid. In order to do filtering in our grid system, two things need to
 * happen:
 *
 *  - Filter tiles based on tile_taxonomy
 *  - Assign new positions to the filtered tiles
 *
 * For filtering we need to first get an actual tile object based on the grid
 * position data. This allows us to find the tile_taxonomy and thus run the
 * logical operator logic against the collection.
 *
 * This is straightforward.
 *
 * However, assigning new positions is a bit of black magic. Tiles are
 * absolutely positioned in a grid div based on their column, row, sizex, and
 * sizey. Leaving the original positions would result in a grid which simply
 * hides the tiles rather than reshuffling the items into the best view. It
 * also throws off the height math significantly.
 *
 * Because of this we build a new index of assigned positions in the grid. We
 * use that for each grid position left over to find where it can fit in the
 * new, filtered, grid. As a result we have a grid which appears to reshuffle
 * in a smart way while maintaining the same constraints unfiltered grids
 * would.
 *
 * All of this logic is non-destructive in nature and thus resetting the
 * filters will yield the original grid again.
 */
const getFilteredGridPositions = (state, columnCount, gridPositions, appliedTagIds, filterLogicalOperator)=> {
  const newlyAssignedGridPositions = [];

  const filteredGridPositions = flow([
    (gridPos)=> filter(gridPos, (gp)=> {
      const tile = generateTileFromGridPosition(state, gp);
      const tileTaxonomyTagIds = get(tile, 'tileTaxonomy.tagIds', []);

      if (filterLogicalOperator === GRID_TAXONOMY_FILTER_LOGICAL_OPERATOR_OR) {
        return !isEmpty(intersection(appliedTagIds, tileTaxonomyTagIds));
      }

      return size(difference(appliedTagIds, tileTaxonomyTagIds)) === 0;
    }),

    (gridPos)=> map(gridPos, (gp)=> {
      const newPosition = getNextAvailablePosition({
        sizex: gp.sizex,
        sizey: gp.sizey,
        gridPositions: newlyAssignedGridPositions,
        columnCount: columnCount
      });

      newlyAssignedGridPositions.push(newPosition);

      return { ...gp, ...newPosition };
    })
  ])(gridPositions);

  return filteredGridPositions;
};

export const mapStateToProps = (state, { section, subpage, spacingRowsAtBottomCount, tabSpacing }) => {
  const renderAsMobile = getRenderAsMobile(state);
  const gridTaxonomy = getSectionGridTaxonomy(state, section);
  const appliedTagIdsByCategoryId = getAppliedTagIdsByCategoryIdForSection(state, section);
  const appliedTagIds = flatten(values(appliedTagIdsByCategoryId)) || [];
  const tileSpacingStyle = getTileSpacingStyle(section);

  let gridPositions = sortTiles(compact(section.gridPositions));

  if(renderAsMobile){
    const tilePriorityByUuid = getTileMobileOrderPriorityByUuid(state, section);
    gridPositions = sortBy(gridPositions, ({ uuid }) => -(tilePriorityByUuid[uuid] || 0));
  }

  var areFilterResultsEmpty = false;

  // we only want this to apply if we actually have filters applied
  if (!isEmpty(gridPositions) && !isEmpty(appliedTagIds)) {
    gridPositions = getFilteredGridPositions(
      state,
      section.columnCount,
      gridPositions,
      appliedTagIds,
      gridTaxonomy.filterMeta.logicalOperator
    );

    if (isEmpty(gridPositions)) {
      areFilterResultsEmpty = true;
    }
  }

  return {
    areFilterResultsEmpty,
    gridTaxonomy,
    gridPositions,
    subpage,
    tileSpacingStyle,
    columnCount: section.columnCount,
    shouldShowGridLines: false, // we never want these to show in the viewer
    renderAsMobile,
    tabSpacing,
    spacingRowsAtBottomCount: (
      spacingRowsAtBottomCount ?
        spacingRowsAtBottomCount
      : areFilterResultsEmpty ?
        1
      :
        0
    )
  };
};

const GridSectionLayoutDeterminer = ({ renderAsMobile, tabSpacing, isTabActive, ...props }) => {
  if (renderAsMobile) {
    return (
      <GridSectionMobileLayout {...props} />
    );
  } else {
    return (
      <GridSectionDesktopLayout 
        GridRenderer={GridRendererDesktop}
        tabSpacing={tabSpacing}
        isTabActive={isTabActive}
        {...props} 
      />
    );
  }
};

export const mapDispatchToProps = (dispatch) => ({});

export default connect(
  mapStateToProps,
  mapDispatchToProps

)(GridSectionLayoutDeterminer);
